<template>
    <div class="service-basket flow__panel flow__container-fluid flow__panel--positioned-buttons">
        <div>
            <section>
                <h2>Ecofurb service: a specialist energy review for your home</h2>
                <p>We’re experts in making homes more comfortable, cheaper to run and more climate friendly. We’re here to help, from start to finish.</p>
                <p>Our Ecofurb services offer a specialist review of your home that gives you all the information you need to start making your home more energy efficient. The Ecofurb service includes an in-person home survey, a follow-up call with one of our home energy experts and a detailed report tailored to your home.</p>
                <p>This Ecofurb Plan sets out</p>
                <ul>
                    <li>What will work best for your home and plans</li>
                    <li>How much you can reduce carbon emissions and bills</li>
                    <li>How much works are likely to cost</li>
                </ul>
                <p>We can also connect you to installers who can help make your plans a reality, and check the work to make sure it meets our quality standards.</p>
                <h3>Ecofurb Plus service</h3>
                <p>The standard Ecofurb service, above, with additional services that we recommend if you have a larger home or an older home, or if you think you want to undertake substantial work. including:</p>
                <ul>
                    <li>360 photography, floor plans and elevations to improve the tailoring of advice and quotes to the details of your home.</li>
                    <li>Drone footage to check roof condition for solar panels</li>
                </ul>
            </section>
            <error-handler />
            <section>
                <h4>Choose the service suitable for your property</h4>
                <div v-for="item in serviceProducts"
                     :key="item.serviceTypeId"
                     class="service-basket__service-item service-basket__service-item--divided">
                    <service-product-item :name="item.name"
                                          :cost="item.cost"
                                          :in-basket="item.inBasket"
                                          :disabled="!item.canAdd"
                                          @add="addServiceProductAsync(item)"
                                          @remove="removeServiceProductAsync(item)" />
                </div>
            </section>
            <section>
                <h4>Additional Options</h4>
                <p>You can upgrade your home survey to get more technical detail.</p>
                <service-product-item v-for="item in subServiceProducts"
                                      :key="item.serviceTypeId"
                                      :name="item.name"
                                      :cost="item.cost"
                                      :in-basket="item.inBasket"
                                      :disabled="!item.canAdd"
                                      @add="addServiceProductAsync(item)"
                                      @remove="removeServiceProductAsync(item)">
                    {{ item.description }}
                </service-product-item>
            </section>
        </div>
        <aside>
            <basket :service-basket="serviceBasket" @delete="removeServiceProductAsync" />
        </aside>
    </div>
</template>

<script>

import Vuex from 'vuex'

import ProgressManager from '@/progress-manager'
import ServiceBasketManager from '@/service-basket-manager'
import ErrorHandler from '@/components/app-core/error-handler'
import Basket from '@/components/service-basket/basket'
import ServiceProductItem from '@/components/service-basket/service-product-item'
import arrays from '@/helpers/arrays'
import ServiceProduct from '@/models/service-product'
import { MUTATIONS } from '@/store/mutations'

const ECOFURB = 1
const ECOFURB_PLUS = 2
const THERMAL_IMAGING = 3
const AIR_PRESSURE = 4

export default {
    components: {
        Basket,
        ErrorHandler,
        ServiceProductItem,
    },

    data() {
        return {
            /**
             * @type {Array<ServiceProduct>}
             */
            allServiceProducts: [
                new ServiceProduct(ECOFURB, 'Ecofurb service', 540),
                new ServiceProduct(ECOFURB_PLUS, 'Ecofurb Plus service', 840),
                new ServiceProduct(THERMAL_IMAGING, 'Thermal imaging survey', 240, 'See where your home is losing heat to refine your home energy plans. Available as an addition to either the core or detailed service, October – March, weather dependent.'),
                new ServiceProduct(AIR_PRESSURE, 'Air pressure testing', 99, 'Available as an addition to the detailed house survey, to understand how leaky your home is. It’s particularly recommended when planning a heat pump installation or as a check on completion of insulation to see how well the job has been done. This can be ordered at any point in your project.'),
            ],
        }
    },

    async mounted() {
        ProgressManager.clearVisitedRoute(this.$router.currentRoute.name)
        await ServiceBasketManager.loadBasketAsync()
        this._updateStateFromBasket()
    },

    computed: {
        ...Vuex.mapState({
            /**
             * @param {VuexState} stateStore
             * @returns {ServiceBasket}
             */
            serviceBasket: stateStore => stateStore.serviceBasket.basket,

            /**
             * @param {VuexState} stateStore
             * @returns {Array<ServiceBasketItem>}
             */
            serviceBasketItems: stateStore => [ ...stateStore.serviceBasket.basket.items ]
                .sort(arrays.sortByProperty('serviceTypeId')),
        }),

        /**
         * Return top level service products (those without a description).
         * @returns {Array<ServiceProduct>}
         */
        serviceProducts() {
            return this.allServiceProducts.filter(item => item.description === null)
        },

        /**
         * Return sub-service products (those with a description).
         * @returns {Array<ServiceProduct>}
         */
        subServiceProducts() {
            return this.allServiceProducts.filter(item => item.description !== null)
        },
    },

    methods: {
        /**
         * Update changes to the reactive state based on the state of the basket.
         */
        _updateStateFromBasket() {
            if (!this.serviceBasket) return

            // reset the state of all service products
            this.allServiceProducts = this.allServiceProducts.map(item => ({
                ...item,
                canAdd: item.description === null,
                inBasket: false,
            }))

            // update the state of all the service products based on what is in the service basket
            this.serviceBasketItems.forEach(item => {
                const serviceProduct = this.allServiceProducts.find(({ serviceTypeId }) => serviceTypeId === item.serviceTypeId)

                if (serviceProduct) {
                    // thermal imaging can only be added if ecofurb plus is in the basket
                    if (serviceProduct.serviceTypeId === THERMAL_IMAGING) {
                        const hasEcofurbPlus = this.serviceBasketItems.find(({ serviceTypeId }) => serviceTypeId === ECOFURB_PLUS)

                        if (!hasEcofurbPlus)
                            return

                    }

                    // air pressure can only be added if either ecofurb or ecofurb plus is in the basket
                    if (serviceProduct.serviceTypeId === AIR_PRESSURE) {
                        const hasParentService = this.serviceBasketItems
                            .find(({ serviceTypeId }) => serviceTypeId === ECOFURB_PLUS || serviceTypeId === ECOFURB)

                        if (!hasParentService)
                            return

                    }

                    serviceProduct.inBasket = true
                    serviceProduct.canAdd = true

                    // ecofurb and ecofurb plus are mutually exclusive
                    // ecofurb can only have thermal imaging sub-service products
                    if (serviceProduct.serviceTypeId === ECOFURB) {
                        this._updateServiceItemAvailability(ECOFURB_PLUS, false)
                        const subItem = this.subServiceProducts.find(({ serviceTypeId }) => serviceTypeId !== THERMAL_IMAGING)

                        if (subItem) subItem.canAdd = true
                    }

                    // ecofurb and ecofurb plus are mutually exclusive
                    if (serviceProduct.serviceTypeId === ECOFURB_PLUS) {
                        this._updateServiceItemAvailability(ECOFURB, false)
                        this.subServiceProducts.forEach(bi => {
                            bi.canAdd = true
                        })
                    }
                }
            })
        },

        /**
         * Update the canAdd state of an item.
         * @param {number} itemId
         * @param {boolean} newState
         */
        _updateServiceItemAvailability(itemId, newState) {
            const item = this.serviceProducts.find(({ serviceTypeId }) => serviceTypeId === itemId)

            if (item) {
                item.canAdd = newState

                if (!newState) item.inBasket = false
            }
        },

        /**
         * Add a service item to the basket.
         * @param {ServiceProduct} item
         */
        async addServiceProductAsync(item) {
            this.$store.commit(MUTATIONS.clearAllErrors)
            await ServiceBasketManager.addItemToBasketAsync(item)
            this._updateStateFromBasket()
        },

        /**
         * Remove a service item from the basket.
         * @param {ServiceProduct} item
         */
        async removeServiceProductAsync(item) {
            this.$store.commit(MUTATIONS.clearAllErrors)
            await ServiceBasketManager.removeItemFromBasketAsync(item)

            // remove sub-services when a top level service is removed
            if (item.serviceTypeId === ECOFURB || item.serviceTypeId === ECOFURB_PLUS)
                await Promise.all(this.subServiceProducts.map(subItem => ServiceBasketManager.removeItemFromBasketAsync(subItem)))

            this._updateStateFromBasket()
        },
    },
}

</script>

<style lang="scss">

.service-basket {
    h3 {
        margin-bottom: 0.4rem;
    }

    & section > h4 {
        margin-bottom: 0.4rem;
        margin-top: 3rem;
    }

    aside {
        margin-top: 56px;
    }

    @include element('service-item') {
        @include modifier('divided') {
            &:after {
                content: 'OR';
                font-weight: bold;
                padding-left: $wide-gutter;
            }

            &:last-of-type:after {
                content: none;
            }
        }
    }
}

</style>
